import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { CheckboxFormField, InputFormField } from "components/hookForm";
import { Button, Container, Divider, Toast as toast, TypoGraph } from "components";
import { getWhitelabelConfiguration, sendDisbursementReminderTest } from "api";
import styles from "./disbursementReminders.module.scss";

const BAR = "#0b1f4b";
const BUTTON = "#1a4fa0";

const wholeDays = (value: string) => {
  const days = Number(value);
  if (!Number.isInteger(days) || days < 1) {
    return "Enter a whole number of days";
  }
  return true;
};

const EmailFields = ({
  title,
  daysName,
  prefix,
  which,
  companyId,
  headerImage,
  barColor,
  buttonColor,
  recipient,
  sendingWhich,
  onSendTest,
}: {
  title: string;
  daysName: string;
  prefix: "initialReminder" | "finalReminder";
  which: "initial" | "final";
  companyId?: string;
  headerImage?: string;
  barColor: string;
  buttonColor: string;
  recipient: string;
  sendingWhich: "initial" | "final" | null;
  onSendTest: (which: "initial" | "final") => void;
}) => {
  const { getValues, watch } = useFormContext();
  const email = watch(`disbursementReminderConfig.${prefix}`) || {};
  const companyName = watch("name") || "Company";
  const steps = String(email.steps || "")
    .split("\n")
    .map((step: string) => step.trim())
    .filter(Boolean);

  return (
    <Container className={styles.reminder}>
      <TypoGraph variant="h3" content={title} />
      <InputFormField
        name={daysName}
        label="Days after the disbursement was sent"
        type="number"
        required
        validate={
          prefix === "finalReminder"
            ? (value: string) => {
                const daysCheck = wholeDays(value);
                if (daysCheck !== true) return daysCheck;
                const initial = Number(
                  getValues("disbursementReminderConfig.initialReminderDays")
                );
                return (
                  Number(value) > initial ||
                  "The final reminder must be more days after the disbursement than the initial reminder"
                );
              }
            : wholeDays
        }
      />
      <InputFormField
        name={`disbursementReminderConfig.${prefix}.opening`}
        label="Opening"
        multiline
        rows={3}
        maxLength={2000}
        required
      />
      <InputFormField
        name={`disbursementReminderConfig.${prefix}.heading`}
        label="Heading"
        maxLength={500}
        required
      />
      <InputFormField
        name={`disbursementReminderConfig.${prefix}.steps`}
        label="Steps, one per line"
        multiline
        rows={4}
        maxLength={2000}
        required
      />
      <InputFormField
        name={`disbursementReminderConfig.${prefix}.footnote`}
        label="Footnote"
        maxLength={500}
      />
      <InputFormField
        name={`disbursementReminderConfig.${prefix}.buttonLabel`}
        label="Button label"
        maxLength={80}
        required
      />
      <TypoGraph
        variant="body2"
        content="The button is wording only. This email does not include a deposit link."
      />
      <InputFormField
        name={`disbursementReminderConfig.${prefix}.closing`}
        label="Closing"
        multiline
        rows={3}
        maxLength={2000}
        required
      />
      <div className={styles.preview}>
        <div className={styles.header} style={{ background: barColor }}>
          {headerImage ? <img src={headerImage} alt="" /> : companyName}
        </div>
        <div className={styles.body}>
          <p>{email.opening}</p>
          <p className={styles.heading}>{email.heading}</p>
          {steps.length > 0 && (
            <ol>
              {steps.map((step: string, index: number) => (
                <li key={`${index}-${step}`}>{step}</li>
              ))}
            </ol>
          )}
          {email.footnote && <p className={styles.footnote}>{email.footnote}</p>}
          <div className={styles.button} style={{ background: buttonColor }}>
            {email.buttonLabel || "Button"}
          </div>
          <p>{email.closing}</p>
        </div>
        <div className={styles.footer} style={{ background: barColor }} />
      </div>
      {companyId && (
        <Button
          type="button"
          variant="outlined"
          label={sendingWhich === which ? "Sending..." : "Send test"}
          disabled={sendingWhich !== null || !recipient.trim()}
          onClick={() => onSendTest(which)}
        />
      )}
    </Container>
  );
};

const DisbursementReminders = ({ companyId }: { companyId?: string }) => {
  const { getValues, setValue, watch } = useFormContext();
  const enabled = !!watch("disbursementReminderConfig.enabled");
  const barColor = watch("disbursementReminderConfig.barColor") || BAR;
  const buttonColor = watch("disbursementReminderConfig.buttonColor") || BUTTON;
  const [recipient, setRecipient] = useState("");
  const [sending, setSending] = useState<"initial" | "final" | null>(null);
  const [headerImage, setHeaderImage] = useState("");

  useEffect(() => {
    if (!companyId) {
      setHeaderImage("");
      return;
    }
    getWhitelabelConfiguration(companyId)
      .then((response) => setHeaderImage(response.data?.logoBase64 || ""))
      .catch(() => setHeaderImage(""));
  }, [companyId]);

  const sendTest = (which: "initial" | "final") => {
    if (!companyId || !recipient.trim()) {
      return;
    }
    const prefix = which === "initial" ? "initialReminder" : "finalReminder";
    setSending(which);
    sendDisbursementReminderTest(companyId, {
      recipient: recipient.trim(),
      which,
      email: getValues(`disbursementReminderConfig.${prefix}`) || {},
      barColor,
      buttonColor,
    })
      .then(() => toast({ title: "Test email sent", subTitle: recipient.trim() }))
      .catch(() => undefined)
      .finally(() => setSending(null));
  };

  return (
    <Container className={styles.section}>
      <Divider title="Disbursement" />
      <CheckboxFormField
        name="disbursementReminderConfig.enabled"
        label="Send custom email reminders"
      />
      <TypoGraph
        variant="body2"
        content="Off by default. When on, an initial reminder and a final reminder go to the email entered when the disbursement was created. The header uses the whitelabel logo, or the company name when there is no logo."
      />
      <TypoGraph
        variant="body2"
        content="Placeholders, replaced when the email is sent: {{recipientName}}, {{recipientEmail}}, {{amount}} (dollars), {{companyName}}. A placeholder with no value is left blank."
      />
      {enabled && (
        <>
          {companyId ? (
            <label className={styles.testRow}>
              Test recipient
              <input
                className={styles.testInput}
                type="email"
                value={recipient}
                placeholder="name@example.com"
                onChange={(event) => setRecipient(event.target.value)}
              />
            </label>
          ) : (
            <TypoGraph
              variant="body2"
              content="Save the company, then you can send a test of either reminder."
            />
          )}
          <div className={styles.colors}>
            <label>
              Header and footer
              <input
                className={styles.colorInput}
                type="color"
                value={barColor}
                aria-label="Header and footer color"
                onChange={(event) =>
                  setValue("disbursementReminderConfig.barColor", event.target.value, {
                    shouldDirty: true,
                  })
                }
              />
            </label>
            <label>
              Button
              <input
                className={styles.colorInput}
                type="color"
                value={buttonColor}
                aria-label="Button color"
                onChange={(event) =>
                  setValue("disbursementReminderConfig.buttonColor", event.target.value, {
                    shouldDirty: true,
                  })
                }
              />
            </label>
          </div>
          <EmailFields
            title="Initial reminder"
            daysName="disbursementReminderConfig.initialReminderDays"
            prefix="initialReminder"
            which="initial"
            companyId={companyId}
            headerImage={headerImage}
            barColor={barColor}
            buttonColor={buttonColor}
            recipient={recipient}
            sendingWhich={sending}
            onSendTest={sendTest}
          />
          <EmailFields
            title="Final reminder"
            daysName="disbursementReminderConfig.finalReminderDays"
            prefix="finalReminder"
            which="final"
            companyId={companyId}
            headerImage={headerImage}
            barColor={barColor}
            buttonColor={buttonColor}
            recipient={recipient}
            sendingWhich={sending}
            onSendTest={sendTest}
          />
        </>
      )}
    </Container>
  );
};

export default DisbursementReminders;
